// features/onboarding/onboardingGate.ts — the pure decision behind the
// first-run redirect (1.24.00 B1), kept out of the shell so it can be pinned
// without mounting the whole layout.
//
// The rule, in full:
//   isAuthenticated && isEmailVerified && user.onboardedAt === null && !deferred
//
//   • STRICT null. `undefined`/absent must NEVER redirect: every pre-1.24 test
//     fixture seeds users without the field, older cached payloads lack it, and
//     public payloads never carry it. Only a server that explicitly said "this
//     member has not been onboarded" (null) may send someone to /onboarding.
//   • Email-verified only. Steps 2 and 3 are IsEmailVerified writes on the
//     server, so an unverified member sees the VerifyEmailBanner first and the
//     flow only after confirming.
//   • Deferred is per-tab session state ("Remind me later"), never persisted —
//     completion itself lives on the server (`onboardedAt`), which is what makes
//     the flow resumable across devices.

import type { User } from '@/core/models';
import { isOnboardingDeferred } from '@/core/lib/onboardingDeferral';

// The session-storage helpers live in core/lib because authService.signOut()
// must clear the deferral and core/ may never import from features/. They are
// re-exported here so feature code keeps one import site.
export {
  ONBOARDING_DEFER_KEY,
  isOnboardingDeferred,
  deferOnboarding,
  clearOnboardingDeferral,
} from '@/core/lib/onboardingDeferral';

export function shouldRedirectToOnboarding(
  user: Pick<User, 'onboardedAt'> | null | undefined,
  isAuthenticated: boolean,
  isEmailVerified: boolean,
): boolean {
  if (!isAuthenticated || !isEmailVerified || !user) return false;
  if (user.onboardedAt !== null) return false; // strict — undefined never triggers
  return !isOnboardingDeferred();
}
